import de from "@/lib/i18n/de";
import { track } from "@/lib/prerender";

// Sprachpakete: Deutsch steckt im Hauptbundle, alle anderen werden erst bei Bedarf nachgeladen (lib/i18n/*.js)
export const translations = { de };
const PACKS = {
  en: () => import(/* webpackChunkName: "lang-en" */ "@/lib/i18n/en"),
  es: () => import(/* webpackChunkName: "lang-es" */ "@/lib/i18n/es"),
  nl: () => import(/* webpackChunkName: "lang-nl" */ "@/lib/i18n/nl"),
  pl: () => import(/* webpackChunkName: "lang-pl" */ "@/lib/i18n/pl"),
  fr: () => import(/* webpackChunkName: "lang-fr" */ "@/lib/i18n/fr"),
  it: () => import(/* webpackChunkName: "lang-it" */ "@/lib/i18n/it"),
};
const loading = {};
export const loadLangPack = (lang) => {
  if (translations[lang]) return Promise.resolve(translations[lang]);
  if (!PACKS[lang]) return Promise.resolve(translations.de);
  if (!loading[lang]) loading[lang] = track(PACKS[lang]().then((m) => { translations[lang] = m.default; return m.default; }));
  return loading[lang];
};
export const loadAllLangPacks = () => Promise.all(LANG_CODES.map(loadLangPack));
export const ISLANDS = ["tenerife", "gran-canaria", "fuerteventura", "lanzarote", "la-palma", "la-gomera", "el-hierro", "la-graciosa"];
export const ISLAND_PATHS = {
  de: { tenerife: "teneriffa", "gran-canaria": "gran-canaria", fuerteventura: "fuerteventura", lanzarote: "lanzarote", "la-palma": "la-palma", "la-gomera": "la-gomera", "el-hierro": "el-hierro", "la-graciosa": "la-graciosa" },
  en: { tenerife: "tenerife", "gran-canaria": "gran-canaria", fuerteventura: "fuerteventura", lanzarote: "lanzarote", "la-palma": "la-palma", "la-gomera": "la-gomera", "el-hierro": "el-hierro", "la-graciosa": "la-graciosa" },
  es: { tenerife: "tenerife", "gran-canaria": "gran-canaria", fuerteventura: "fuerteventura", lanzarote: "lanzarote", "la-palma": "la-palma", "la-gomera": "la-gomera", "el-hierro": "el-hierro", "la-graciosa": "la-graciosa" },
};
ISLAND_PATHS.nl = { ...ISLAND_PATHS.en };
ISLAND_PATHS.pl = { ...ISLAND_PATHS.en, tenerife: "teneryfa" };
ISLAND_PATHS.fr = { ...ISLAND_PATHS.en };
ISLAND_PATHS.it = { ...ISLAND_PATHS.en };
// Alle Website-Sprachen (Kern DE/EN/ES + NL/PL/FR/IT). Sichtbarkeit im Umschalter/hreflang steuert `publicSiteLangs` (Admin → Sprachen).
export const LANG_CODES = ["de", "en", "es", "nl", "pl", "fr", "it"];
export const CORE_LANGS = ["de", "en", "es"];
export const LANG_LABELS = { de: "Deutsch", en: "English", es: "Español", nl: "Nederlands", pl: "Polski", fr: "Français", it: "Italiano" };
export const LANG_LABELS_DE = { de: "Deutsch", en: "Englisch", es: "Spanisch", nl: "Niederländisch", pl: "Polnisch", fr: "Französisch", it: "Italienisch" };
export const SITE_LANGS = LANG_CODES.map((c) => [c, LANG_LABELS_DE[c]]);
export let publicSiteLangs = [...CORE_LANGS];
export const setPublicSiteLangs = (codes) => { publicSiteLangs = LANG_CODES.filter((c) => codes.includes(c)); };
// Mehrsprachiges Feld auslesen: Sprache → EN (bei NL/PL) → DE
export const tr = (obj, lang) => (obj?.[lang] || (!CORE_LANGS.includes(lang) && obj?.en) || obj?.de || "");
// Fehlende NL/PL/FR/IT-UI-Texte fallen auf EN zurück
const LANG_RE = /^\/(en|es|nl|pl|fr|it)(?=\/|$)/;
export const langFromPath = (pathname = window.location.pathname) => { const m = pathname.match(LANG_RE); return m ? m[1] : "de"; };
export const langPrefix = (lang) => (lang === "de" ? "" : `/${lang}`);
export let currentLang = langFromPath();
export const setCurrentLang = (l) => { currentLang = l; };
export const ISLAND_PATH = new Proxy({}, { get: (_, k) => ISLAND_PATHS[currentLang][k] });
export const ISLAND_BY_PATH = Object.fromEntries(LANG_CODES.flatMap((l) => Object.entries(ISLAND_PATHS[l]).map(([k, v]) => [v, k])));
export const islandUrl = (island, lang = currentLang) => `/${ISLAND_PATHS[lang][island]}`;
export const propertyUrl = (p, lang = currentLang) => {
  const slug = (p.slug_i18n && p.slug_i18n[lang]) || p.slug;  // übersetzte URL-Endung je Sprache, DE = slug
  return ISLAND_PATHS[lang][p.island] ? `/${ISLAND_PATHS[lang][p.island]}/${slug}/` : `/property/${slug}`;
};
// Objektseite: URL-Endungen des gerade angezeigten Objekts, damit Sprachumschalter/hreflang die passende Endung nehmen
let PROP_SLUGS = null;
export const registerPropertySlugs = (p) => { PROP_SLUGS = p ? { de: p.slug, ...(p.slug_i18n || {}), _all: new Set([p.slug, ...(p.slug_aliases || []), ...Object.values(p.slug_i18n || {})]) } : null; };
// Anlagen-/Fincaseite: Segment richtet sich nach der Art (finca → /finca/, sonst /anlage/)
export const complexUrl = (c) => `/${ISLAND_PATHS.de[c.island]}/${c.kind === "finca" ? "finca" : "anlage"}/${c.slug}/`;
// Übersicht aller Fincas & Anlagen (ein Pfad für alle Sprachen, Sprachpräfix kommt vom Router)
export const COMPLEXES_PATH = "fincas-anlagen";
export const complexesUrl = () => `/${COMPLEXES_PATH}`;
export const TIPS_PATH = "reisetipps";
export const tipsUrl = () => `/${TIPS_PATH}`;
export const localizedPath = (pathname, toLang) => {
  const rest = pathname.replace(LANG_RE, "") || "/";
  const segs = rest.split("/");
  const page = PAGE_BY_PATH[segs[1]];
  if (page && !segs[2]) segs[1] = PAGE_PATHS[page][toLang] || PAGE_PATHS[page].de;
  const island = ISLAND_BY_PATH[segs[1]];
  if (island) segs[1] = ISLAND_PATHS[toLang][island];
  if (PROP_SLUGS && segs[2] && PROP_SLUGS._all.has(segs[2])) segs[2] = PROP_SLUGS[toLang] || PROP_SLUGS.de;
  return langPrefix(toLang) + segs.join("/");
};
// Statische Seiten mit übersetztem Pfad je Sprache (alte deutsche Pfade bleiben per Weiterleitung erreichbar)
export const PAGE_PATHS = {
  bewertungen: { de: "bewertungen", en: "reviews", es: "opiniones", nl: "beoordelingen", pl: "opinie", fr: "avis", it: "recensioni" },
};
const PAGE_BY_PATH = Object.fromEntries(Object.entries(PAGE_PATHS).flatMap(([k, m]) => Object.values(m).map((v) => [v, k])));
// Pfad ohne Sprachpräfix (für <Link>, der Router hat die Sprache als basename)
export const pagePath = (key, lang = currentLang) => `/${PAGE_PATHS[key][lang] || PAGE_PATHS[key].de}`;
export const PROPERTY_TYPES = ["apartment", "studio", "villa", "finca"];
// Settings-Feld mit Sprach-Suffix (key_de/key_en/…) inkl. EN-Rückfall für NL/PL
export const trs = (s, key, lang) => s?.[`${key}_${lang}`] || (!CORE_LANGS.includes(lang) && s?.[`${key}_en`]) || s?.[`${key}_de`] || "";
// Paket der aktuellen Sprache sofort anfordern, damit es parallel zu den Einstellungen lädt
if (currentLang !== "de") loadLangPack(currentLang);
